import { noTouch } from '@/assets/styles/mixins.css.js';
import styled, { css } from 'styled-components';
import { keyframes } from 'styled-components';

export const move = keyframes`
  0% {
    transform: none;
  }

  25% {
    transform: scale(1.2) translateX(50px);
  }

  50% {
    transform: scale(0.8);
  }

  75% {
    transform: scale(1.2) translateX(-50px);
  }

  100% {
    transform: none;
  }
`;

const Button = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: var(--color-decor);
  width: fit-content;
  min-height: 28px;
  border-radius: 90px;
  padding: 5px 15px;
  font-family: var(--font-default);
  font-size: 12px;
  color: #fff;
  text-transform: uppercase;
  user-select: none;
  cursor: pointer;
  transition: var(--button-transition, background 0.25s ease-out);

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    opacity: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    box-shadow: 0px 4px 8px rgba(17, 33, 70, 0.16);
    transition: opacity 0.3s ease-out;
  }

  &:active {
    background: var(--button-active-bg-color, #aaa);
  }

  &:focus {
    background: var(--button-focus-bg-color, #aaa);
  }

  ${noTouch()`
    &:hover {
      &::after {
        opacity: 1;
      }
    }
  `}

  ${(props) =>
    props.light &&
    css`
      border: 1px solid var(--color-decor);
      padding: 4px 14px;
      color: var(--color-decor, #b69b64);
      background: var(--button-bg-color, #fff);
      transition: var(
        --button-transition,
        background 0.25s ease-out,
        color 0.25s ease-out,
        border-color 0.25s ease-out
      );

      &::after {
        content: none;
      }

      &:focus {
        background: var(--button-focus-bg-color, #f8f8f8);
      }

      &:active {
        border-color: var(--button-active-text-color, #aaa);
        color: var(--button-active-text-color, #aaa);
        background: var(--button-focus-bg-color, #f8f8f8);
      }

      ${noTouch()`
        &:hover {
          border-color: var(--button-active-text-color, #aaa);
          color: var(--button-hover-text-color, #aaa);
          background: var(--button-hover-bg-color, #F8F8F8);
        }
      `}
    `}

  ${(props) =>
    props.lightFilled &&
    css`
      background: #f5f5f5;
      padding: 10px 20px !important;
      min-height: 40px !important;
      color: var(--color-decor, #b69b64);
      font-weight: 500;
      font-size: 14px;
      line-height: 143%;

      &::after {
        content: none;
      }

      &:focus {
        color: #fff;
        background: var(--color-decor);
      }

      &:active {
        color: #fff;
        background: var(--color-decor);
      }

      ${noTouch()`
      &:hover {
        color: #fff;
        background: var(--color-decor);
      }
    `}
    `}

  ${(props) =>
    props.size == 'max' &&
    css`
      padding: 14px 35px;
      font-size: 16px;
      min-height: 48px;
    `}

  ${(props) =>
    props.size == 'max' &&
    props.light &&
    css`
      padding: 13px 34px;
    `}

   ${(props) =>
    props.size == 'large' &&
    css`
      padding: 12px 28px;
      font-size: 13px;
      min-height: 44px;
    `}

  ${(props) =>
    props.size == 'large' &&
    props.light &&
    css`
      padding: 11px 27px;
    `}

  ${(props) =>
    props.size == 'mid' &&
    css`
      padding: 11px 20px;
      font-size: 14px;
      min-height: 42px;
    `}

  ${(props) =>
    props.size == 'mid' &&
    props.light &&
    css`
      padding: 10px 19px;
    `} 

  ${(props) =>
    props.minilight &&
    css`
      padding-top: var(--button-padding-top, 10px);
      padding-bottom: var(--button-padding-bottom, 12px);
      padding-left: var(--button-padding-left, 20px);
      padding-right: var(--button-padding-right, 20px);
      font-weight: normal;
      font-size: 16px;
      color: var(--button-text-color, var(--color-decor-dark));
      text-transform: none;
      border: none;
      background: var(--button-bg-color, var(--color-bg-accent));
      transition: var(--button-transition, background 0.25s ease-out, color 0.25s ease-out);

      &::after {
        content: none;
      }

      &:focus {
        background: var(--button-focus-bg-color, #ebeef5);
      }

      &:active {
        color: var(--button-active-text-color, var(--color-decor));
        background: var(--button-focus-bg-color, #ebeef5);
      }

      ${noTouch()`
        &:hover {
          color: var(--button-hover-text-color, var(--color-decor-hover));
          background: var(--button-hover-bg-color, #EBEEF5);
        }
      `}
    `}

  ${(props) =>
    props.isLoading &&
    css`
      ${Button.Loader} {
        opacity: 1;
        pointer-events: all;
        visibility: visible;
        transition: visibility 0s, opacity 0.3s ease-out;
      }
    `}

  ${(props) =>
    props.arrow &&
    css`
      &::before {
        content: '';
        display: block;
        width: 18px;
        height: 12px;
        margin-left: 5px;
        order:1;
        background-image: url("data:image/svg+xml,%3Csvg width='18' height='12' viewBox='0 0 18 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.5625 11.562L11.1244 6.00007L5.5625 0.438182' stroke='white' /%3E%3Cpath d='M11.2637 11.5619L16.8256 6.00001L11.2637 0.438121' stroke='white' /%3E%3C/svg%3E");        
        background-repeat: no-repeat;
        background-size: 100% 100%;
        background-position: center center;
        transition: transform 0.3s ease-out;
      }
      ${noTouch()`
        &:hover::before {
          transform:translateX(5px);
        }
      `}
    `}

  ${(props) =>
    props.arrow && props.light &&
    css`
      &::before {
        background-image: url("data:image/svg+xml,%3Csvg width='18' height='12' viewBox='0 0 18 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M5.56189 11.562L11.1238 6.00013L5.56189 0.438243' stroke='%23B69B64' /%3E%3Cpath d='M11.2628 11.5615L16.8247 5.99964L11.2628 0.437755' stroke='%23B69B64' /%3E%3C/svg%3E");      
      }
      ${noTouch()`
        &:hover {
          border-color: var(--color-decor);
          color: var(--color-decor);
          background: var(--button-bg-color, #fff);
        }
      `}
  `}
`;

Button.Loader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: var(--button-radius, 32px);
  background: var(--button-loader-color, var(--color-border));
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: visibility 0s 0.3s, opacity 0.3s ease-out;

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 5px);
    left: calc(50% - 5px);
    width: 10px;
    height: 10px;
    background: #fff;
    border-radius: 50%;
    animation: ${move} 3s linear infinite;
  }
`;

export default Button;
